import React, { useEffect, useState, useCallback } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { toast } from "react-toastify";
import { updateCms, getCms } from "api/services/cmsServices";
import TextField from "components/fields/TextField";

// FeatureCard component memoized to prevent unnecessary re-renders
const FeatureCard = React.memo(({ featureKey, feature, errors, onChange, onImageUpload, onSave, id }) => (
  <Card extra={"w-full h-full p-6"}>
    <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">Feature {featureKey.slice(-1)}</h3>
    <InputField
      label="Title*"
      placeholder="Enter Title"
      id={`${featureKey}-title`}
      type="text"
      value={feature.title}
      onChangeFun={(e) => onChange(featureKey, "title", e.target.value)}
    />
    {errors?.title && <p className="mb-2 ml-2 text-sm text-red-500">{errors?.title}</p>}

    <div className="mt-4">
      <TextField
        label="Description*"
        value={feature.description}
        onChangeFun={(e) => onChange(featureKey, "description", e.target.value)}
        className="mt-2 w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none"
        rows="4"
        placeholder="Enter description"
      />
    </div>
    {errors?.description && <p className="mb-2 ml-2 text-sm text-red-500">{errors?.description}</p>}

    <div className="mt-4">
      <label className="text-sm font-bold text-navy-700 dark:text-white">Upload Image</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => onImageUpload(featureKey, e)}
        className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
      />
      {feature.imgPreview && (
        <img
          src={feature.imgPreview}
          alt={`Feature ${featureKey.slice(-1)} Preview`}
          className="mt-4 h-40 rounded-lg border object-contain"
        />
      )}
    </div>

    <div className="flex justify-end mt-6">
      <button
        onClick={() => onSave(featureKey, id)}
        className="text-md rounded-md bg-brand-500 px-4 py-1.5 font-semibold text-white shadow transition hover:bg-brand-600"
      >
        Save Feature {featureKey.slice(-1)}
      </button>
    </div>
  </Card>
));

const Features = () => {
  const [features, setFeatures] = useState({
    feature1: { title: "", description: "", img: "", imgPreview: "" },
    feature2: { title: "", description: "", img: "", imgPreview: "" },
    feature3: { title: "", description: "", img: "", imgPreview: "" },
  });

  const [errors, setErrors] = useState({
    feature1: {},
    feature2: {},
    feature3: {},
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const [response1, response2, response3] = await Promise.all([
        getCms("feature_1"),
        getCms("feature_2"),
        getCms("feature_3"),
      ]);

      setFeatures({
        feature1: {
          title: response1?.data?.data?.title || "",
          description: response1?.data?.data?.description || "",
          img: "",
          imgPreview: response1?.data?.data?.image || "",
        },
        feature2: {
          title: response2?.data?.data?.title || "",
          description: response2?.data?.data?.description || "",
          img: "",
          imgPreview: response2?.data?.data?.image || "",
        },
        feature3: {
          title: response3?.data?.data?.title || "",
          description: response3?.data?.data?.description || "",
          img: "",
          imgPreview: response3?.data?.data?.image || "",
        },
      });
    } catch (error) {
      toast.error("Failed to fetch features data");
    }
  };

  const handleChange = useCallback((featureKey, field, value) => {
    setFeatures((prev) => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [field]: value,
      },
    }));
    setErrors((prev) => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [field]: "",
      },
    }));
  }, []);

  const handleImageUpload = useCallback((featureKey, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange(featureKey, "img", file);
        handleChange(featureKey, "imgPreview", reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, [handleChange]);

  const handleSave = useCallback(async (featureKey, id) => {
    const currentFeature = features[featureKey];
    const newErrors = {};
    let hasErrors = false;

    if (!currentFeature.title.trim()) {
      newErrors.title = "Title is required";
      hasErrors = true;
    }
    if (!currentFeature.description.trim()) {
      newErrors.description = "Description is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors((prev) => ({
        ...prev,
        [featureKey]: newErrors,
      }));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("type", `feature_${id}`);
      formData.append('title', currentFeature?.title);
      formData.append('description', currentFeature?.description);
      formData.append("image", currentFeature?.img)

      const response = await updateCms(formData);
      if (response?.status === 200) {
        toast.success(`Feature ${featureKey.slice(-1)} updated successfully`);
      } else {
        toast.error(`Failed to update feature ${featureKey.slice(-1)}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Something went wrong");
    }
  }, [features]);

  return (
    <div className="mt-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FeatureCard
          featureKey="feature1"
          feature={features.feature1}
          errors={errors.feature1}
          onChange={handleChange}
          onImageUpload={handleImageUpload}
          onSave={handleSave}
          id="1"
        />
        <FeatureCard
          featureKey="feature2"
          feature={features.feature2}
          errors={errors.feature2}
          onChange={handleChange}
          onImageUpload={handleImageUpload}
          onSave={handleSave}
          id="2"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-1">
          <FeatureCard
            featureKey="feature3"
            feature={features.feature3}
            errors={errors.feature3}
            onChange={handleChange}
            onImageUpload={handleImageUpload}
            onSave={handleSave}
            id="3"
          />
        </div>
      </div>
    </div>
  );
};

export default Features;
